import { applyStylesIf, cx } from 'utils/cx'
import { usePricesStore } from '../../context/PricesProvider'
import { PRICE_KEYS } from '../../keys'
import type { ExtraPriceProps } from '../../types'
import {
	getCrossedOutAdditionalPriceClassName,
	getCrossedOutPriceClassName,
} from '../../utils/getStyles/getStyles'
import { PriceInfo } from '../PriceInfo/PriceInfo'
import { SinglePrice } from '../SinglePrice/SinglePrice'
import styles from './Price.module.css'

/**
 * @description
 * Internal component to render prices
 *
 * @warning Do not use this component directly to render prices
 *
 * @see {@link ClientPrice}
 * @see {@link ServerPrice}
 * @see {@link ClientProductPrice}
 * @see {@link ServerProductPrice}
 */
export function Price({ dataTestId }: Pick<ExtraPriceProps, 'dataTestId'>) {
	const {
		price,
		hideSaleOrPromoPrice,
		crossedOutPrices = [],
		allCrossedOutWithoutLowest = [],
		priceRef,
		priceClasses,
		horizontal = false,
		align = 'start',
	} = usePricesStore()

	const {
		additionalPrice: additionalPriceClass,
		price: priceClass,
		wrapper: wrapperClass,
	} = priceClasses

	const filteredCrossedOut = Array.from(
		new Map(
			crossedOutPrices
				.filter((p) => p.amount !== price.amount)
				.map((crossed) => [crossed.amount, crossed])
		).values()
	)

	const additionalPriceClassName = additionalPriceClass ?? priceClass

	const crossedOut =
		hideSaleOrPromoPrice && filteredCrossedOut.length > 0
			? allCrossedOutWithoutLowest
			: filteredCrossedOut

	const { additionalPrices } = price

	return (
		price && (
			<div
				ref={priceRef}
				className={cx(
					wrapperClass,
					styles.container,
					styles[align],
					applyStylesIf(horizontal, styles.horizontalContainer),
					applyStylesIf(additionalPrices !== undefined, styles.additionalPrice)
				)}
				data-testid={dataTestId ?? 'prices.container'}
			>
				<PriceInfo crossedOut={filteredCrossedOut}>
					{crossedOut.length > 0 &&
						crossedOut.map((crossedOutPrice, index) => {
							const dataTestSuffix = index >= 1 ? `-${index}` : ''
							return (
								<SinglePrice
									priceClassName={getCrossedOutPriceClassName({
										classes: priceClasses,
										index,
									})}
									additionalPriceClassName={getCrossedOutAdditionalPriceClassName(
										{ classes: priceClasses, index }
									)}
									key={crossedOutPrice.amount.toString()}
									price={crossedOutPrice}
									label={PRICE_KEYS[index + 1]}
									isCrossedOut
									dataTestid={`crossedOutPrice${dataTestSuffix}`}
								/>
							)
						})}

					<SinglePrice
						priceClassName={priceClass}
						price={price}
						additionalPriceClassName={additionalPriceClassName}
						label={PRICE_KEYS[0]}
						finalPrice={crossedOut.length > 0}
						dataTestid='currentPrice'
					/>
				</PriceInfo>
			</div>
		)
	)
}
